import StoreMessage from '../../redux/models/Message'
import {
  MESSAGE_CATEGORY_IDS,
  resolveMessageNotificationCategoryId
} from '../../constants/messageCategoryIds'

function resolveMainChatMessageColor (categoryId, typeMessage, messageColorRaw) {
  const typeStr = String(typeMessage || '1')
  if (typeStr === '4' || categoryId === MESSAGE_CATEGORY_IDS.WARNING) {
    return 'warning'
  }
  if (categoryId === MESSAGE_CATEGORY_IDS.DANGER) {
    return 1
  }
  if (categoryId === MESSAGE_CATEGORY_IDS.SUCCESS) {
    return 2
  }
  const color =
    messageColorRaw == null || messageColorRaw === ''
      ? 0
      : typeof messageColorRaw === 'number'
        ? messageColorRaw
        : parseInt(String(messageColorRaw), 10) || 0
  return color
}

function parseReceiptCount (value) {
  if (value === true || value === 'true') return 1
  if (value === false || value === 'false' || value == null) return 0
  const n = parseInt(value, 10)
  return Number.isFinite(n) && n > 0 ? n : 0
}

/**
 * Maps legacy main-chat receipt fields (message_forwarded / read_message / total_message)
 * to store flags used by MessageBubble (delivery + read + mass counts).
 */
export function mapMainChatReceiptFields (raw = {}) {
  if (raw.received === true || raw.read === true) {
    const readCount = parseReceiptCount(raw.readCount ?? raw.read_message ?? raw.readByMe)
    const totalRecipients = parseReceiptCount(raw.totalRecipients ?? raw.total_message)
    return {
      received: !!raw.received || !!raw.read,
      read: !!raw.read,
      readCount: readCount || undefined,
      totalRecipients: totalRecipients || undefined
    }
  }

  const forwardedCount = parseReceiptCount(
    raw.message_forwarded ?? raw.messageForwarded ?? raw.message_is_forwarded
  )
  const readCount = parseReceiptCount(raw.read_message ?? raw.readMessage ?? raw.readByMe)
  const totalRecipients = parseReceiptCount(raw.total_message ?? raw.totalMessage)
  const isMass = totalRecipients >= 2

  return {
    received: forwardedCount > 0,
    read: isMass
      ? totalRecipients > 0 && readCount >= totalRecipients
      : readCount > 0,
    readCount: readCount || undefined,
    totalRecipients: totalRecipients || undefined
  }
}

/**
 * Normalizes the "messages created" socket payload to the store message shape.
 * @param {object} payload - Raw payload from socket (id_message, body_message, type_message, etc.)
 * @returns {object|null} Normalized message for MessageDataStoreSlice or null
 */
export function normalizeMessagesCreatedPayload (payload) {
  if (!payload || payload.id_message == null) return null
  const typeMessage = String(payload.type_message || '1')
  const type = StoreMessage.SUPPORTED_MESSAGE_TYPES.TEXT
  const categoryId = resolveMessageNotificationCategoryId({
    categoryId: payload.category_id ?? payload.categoryId,
    categoryName: payload.category_name ?? payload.categoryName,
    typeMessage,
    message_color: payload.message_color,
    danger_message: payload.danger_message
  })
  const color = resolveMainChatMessageColor(categoryId, typeMessage, payload.message_color)
  const monitorTarget = payload.monitor_message_uuid_device_target || undefined
  const receiverDeviceId =
    monitorTarget ||
    (payload.uuid_device_target && payload.uuid_device_target !== 'massmessage' && payload.uuid_device_target !== 'monitor_message'
      ? payload.uuid_device_target
      : undefined) ||
    payload.receiver_device_ids?.[0] ||
    undefined
  const senderDeviceId = (() => {
    const wireSender =
      payload.uuid_device_sender ??
      (payload.uuid_device != null &&
      String(payload.uuid_device).toLowerCase() !== 'system' &&
      String(payload.uuid_device).toLowerCase() !== payload.uuid_device_target
        ? payload.uuid_device
        : undefined)
    if (wireSender != null && String(wireSender).toLowerCase() === 'operator') {
      return 'operator'
    }
    if (
      (typeMessage === '2' || typeMessage === '3') &&
      payload.id_sender_user != null &&
      payload.id_sender_user !== ''
    ) {
      return 'operator'
    }
    const deviceDataUuid = payload.device_data?.uuid_device ?? payload.device_data?.id
    if (deviceDataUuid != null && String(deviceDataUuid).toLowerCase() === 'operator') {
      return 'operator'
    }
    if (
      (!wireSender || String(wireSender).toLowerCase() === 'system') &&
      monitorTarget
    ) {
      return String(monitorTarget)
    }
    return wireSender ?? undefined
  })()
  return {
    id: payload.id_message,
    content: payload.body_message ?? '',
    type,
    categoryId,
    typeMessage,
    senderDeviceId,
    senderUserId: payload.id_sender_user ?? undefined,
    receiverDeviceId,
    receiverUserId: payload.receiverUserId ?? undefined,
    receiverChatGroupId: payload.receiverChatGroupId ?? undefined,
    categoryName: payload.category_name ?? payload.categoryName ?? undefined,
    createdAt: payload.ondate_message ? new Date(payload.ondate_message) : new Date(),
    color,
    total_message: payload.total_message,
    ...mapMainChatReceiptFields({
      read_message: payload.read_message,
      message_forwarded: payload.message_forwarded,
      total_message: payload.total_message
    })
  }
}

/**
 * Normalizes "chatGroupMessage created" (and similar) socket payloads.
 */
export function normalizeChatGroupMessageCreatedPayload (payload, messageCategoriesById) {
  if (!payload) return null
  const raw = payload?.message || payload?.data?.message || payload
  if (!raw) return null

  const id = raw.id || raw.messageId || raw.uuid || raw._id
  if (!id) return null

  const categoryName = String(
    raw.categoryName ||
    raw.category?.name ||
    raw.category ||
    ''
  ).toLowerCase()
  const typeMessage = raw.typeMessage != null ? String(raw.typeMessage) : undefined
  const categoryId = resolveMessageNotificationCategoryId({
    categoryId: raw.categoryId ?? raw.category?.id,
    categoryName,
    typeMessage,
    message_color: raw.message_color ?? raw.messageColor ?? raw.color,
    danger_message: raw.danger_message ?? raw.dangerMessage
  })

  const receipt = mapChatGroupReceiptFields(raw)

  return {
    ...raw,
    id,
    content: raw.content ?? raw.body ?? raw.text ?? raw.body_message ?? '',
    type: raw.type || StoreMessage.SUPPORTED_MESSAGE_TYPES.TEXT,
    categoryId,
    categoryName: categoryName || undefined,
    typeMessage,
    color:
      categoryId === MESSAGE_CATEGORY_IDS.WARNING
        ? 'warning'
        : categoryId === MESSAGE_CATEGORY_IDS.DANGER
          ? 'error'
          : raw.color,
    receiverChatGroupId: raw.receiverChatGroupId || raw.chatGroupId || raw.receiver_chat_group_id,
    senderUserId: raw.senderUserId || raw.senderUser?.id,
    senderDeviceId: (() => {
      const rawId = raw.senderDeviceId || raw.senderDevice?.id
      if (rawId != null) {
        const normalized = String(rawId).toLowerCase()
        if (normalized === 'operator' || normalized === 'system') return normalized
      }
      const senderName = String(
        raw.senderDevice?.name || raw.deviceSenderName || raw.device_sender_name || ''
      )
        .toLowerCase()
        .trim()
      const senderRolePrefix = senderName.split(':')[0].trim()
      if (
        senderName === 'operator' ||
        senderName === 'agent' ||
        senderName === 'client' ||
        senderName === 'super admin' ||
        senderRolePrefix === 'operator' ||
        senderRolePrefix === 'agent' ||
        senderRolePrefix === 'client' ||
        senderRolePrefix === 'super admin'
      ) {
        return 'operator'
      }
      return rawId
    })(),
    readByMe: raw.readByMe,
    readByAllReceivers: raw.readByAllReceivers,
    ...receipt
  }
}

/**
 * Maps chat group readByMe / readByAllReceivers to store received/read flags.
 */
export function mapChatGroupReceiptFields (raw) {
  const readByAll = raw?.readByAllReceivers === true || raw?.readByAllReceivers === 'true'
  const readByMe = raw?.readByMe === true || raw?.readByMe === 'true'
  return {
    received: readByAll || readByMe,
    read: readByAll
  }
}
